const ALLOWED_TAGS = new Set([
  'b',
  'strong',
  'i',
  'em',
  'u',
  's',
  'strike',
  'code',
  'br',
  'p',
  'span',
  'ul',
  'ol',
  'li',
  'a',
]);

/**
 * Sanitizes HTML for comment content. Allows safe inline/block tags
 * (b, strong, i, em, u, s, strike, code, br, p, span, ul, ol, li, a).
 * For <a>, only href (http(s) URLs) is preserved. Replaces <img> with
 * its alt text or "[Image]". Strips all other tags and attributes.
 * Converts span with text-decoration: line-through to <s>.
 */
export function sanitizeCommentHtml(html: string): string {
  if (!html || typeof html !== 'string') return '';
  const doc = new DOMParser().parseFromString(html, 'text/html');
  const out: string[] = [];

  function walk(node: Node): void {
    if (node.nodeType === Node.TEXT_NODE) {
      out.push(node.textContent ?? '');
      return;
    }
    if (node.nodeType !== Node.ELEMENT_NODE) return;
    const el = node as Element;
    const tag = el.tagName.toLowerCase();
    if (!ALLOWED_TAGS.has(tag)) {
      if (tag === 'img') {
        const alt = el.getAttribute('alt');
        out.push(
          typeof alt === 'string' && alt.trim() !== '' ? alt.trim() : '[Image]',
        );
        return;
      }
      Array.from(node.childNodes).forEach(walk);
      return;
    }
    if (tag === 'br') {
      out.push('<br>');
      return;
    }
    // Normalize span with text-decoration: line-through to <s> so strikethrough is preserved
    const emitTag =
      tag === 'span' &&
      typeof el.getAttribute('style') === 'string' &&
      /text-decoration:\s*line-through/i.test(el.getAttribute('style') ?? '')
        ? 's'
        : tag;
    if (tag === 'a') {
      const href = el.getAttribute('href');
      const safeHref =
        href && typeof href === 'string' && href.startsWith('http')
          ? href
          : '#';
      out.push(
        `<${emitTag} href="${safeHref.replace(/"/g, '&quot;')}" target="_blank" rel="noopener noreferrer">`,
      );
    } else {
      out.push(`<${emitTag}>`);
    }
    Array.from(node.childNodes).forEach(walk);
    out.push(`</${emitTag}>`);
  }

  walk(doc.body);
  return out.join('');
}

/** Returns true if the string looks like HTML (contains tags). */
export function isCommentHtml(text: string): boolean {
  return /<[a-z][\s\S]*>/i.test(text);
}

/** Markdown image pattern so we can remove it from draft/submit when using attachments. */
const MARKDOWN_IMAGE_RE = /!\[[^\]]*\]\([^)]*\)/g;

/**
 * Removes all <img> tags from HTML. Use for new-comment draft and submit so
 * images are only sent via the attachments array, not duplicated in the text.
 */
export function stripCommentHtmlImages(html: string): string {
  if (!html || typeof html !== 'string') return '';
  const doc = new DOMParser().parseFromString(html, 'text/html');
  const out: string[] = [];

  function walk(node: Node): void {
    if (node.nodeType === Node.TEXT_NODE) {
      out.push(node.textContent ?? '');
      return;
    }
    if (node.nodeType !== Node.ELEMENT_NODE) return;
    const el = node as Element;
    const tag = el.tagName.toLowerCase();
    if (tag === 'img') return;
    if (!ALLOWED_TAGS.has(tag)) {
      Array.from(node.childNodes).forEach(walk);
      return;
    }
    if (tag === 'br') {
      out.push('<br>');
      return;
    }
    const emitTag =
      tag === 'span' &&
      typeof el.getAttribute('style') === 'string' &&
      /text-decoration:\s*line-through/i.test(el.getAttribute('style') ?? '')
        ? 's'
        : tag;
    if (tag === 'a') {
      const href = el.getAttribute('href');
      const safeHref =
        href && typeof href === 'string' && href.startsWith('http')
          ? href
          : '#';
      out.push(`<${emitTag} href="${safeHref.replace(/"/g, '&quot;')}">`);
    } else {
      out.push(`<${emitTag}>`);
    }
    Array.from(node.childNodes).forEach(walk);
    out.push(`</${emitTag}>`);
  }

  walk(doc.body);
  let result = out.join('');
  result = result.replace(MARKDOWN_IMAGE_RE, '');
  return result;
}
